import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import Header from "../../Components/Header";
import { fetchServiceDocumentation } from "../../Services/ServiceDocumentation/ServiceDoct";
import { Link } from "react-router-dom";
import { FaCopy } from "react-icons/fa";
import html2pdf from "html2pdf.js";
import { IoMdDownload } from "react-icons/io";
import { fetchProfile } from "../../Services/profile/profileservice";
import axios from "axios";
import { CopyIcon } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const ApiLink = () => {
  const [documentationData, setDocumentationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [serviceId, setServiceId] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({});
  const [responseData, setResponseData] = useState(null);
  const [errorData, setErrorData] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);

  //  NEW: State for method selection
  const [selectedMethod, setSelectedMethod] = useState("");
  const [hasMethods, setHasMethods] = useState(false);
  const [availableMethods, setAvailableMethods] = useState([]);
  const inputRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const apiIdRef = useRef(null);
  const apiKeyRef = useRef(null);
  const tokenIdRef = useRef(null);
  const pdfRef = useRef(null);

  // ✅ Handles BOTH methods.generateToken AND root-level fetchDetails
  const keyDescription = useMemo(() => {
    try {
      return documentationData?.key_description
        ? JSON.parse(documentationData.key_description)
        : {};
    } catch (error) {
      console.error("Error parsing key_description:", error);
      return {};
    }
  }, [documentationData?.key_description]);

  //  FIXED: Correctly handles generateToken (nested) + fetchDetails (root)
  const getFieldConfig = useCallback(
    (fieldName) => {
      let config = {};

      // 1 Method-specific (generateToken inside methods)
      if (
        hasMethods &&
        selectedMethod &&
        keyDescription.methods?.[selectedMethod]
      ) {
        config = keyDescription.methods[selectedMethod][fieldName] || {};
      }
      // 2 Root-level method (fetchDetails at root level)
      else if (hasMethods && selectedMethod && keyDescription[selectedMethod]) {
        config = keyDescription[selectedMethod][fieldName] || {};
      }
      // 3 Non-method APIs (direct root fields)
      else if (!hasMethods && keyDescription[fieldName]) {
        config = keyDescription[fieldName];
      }

      return config;
    },
    [keyDescription, hasMethods, selectedMethod],
  );

  const isFieldRequired = useCallback(
    (fieldName) => {
      return getFieldConfig(fieldName).is_required === 1;
    },
    [getFieldConfig],
  );

  const isFieldReadOnly = useCallback(
    (fieldName) => {
      return getFieldConfig(fieldName).is_readonly === 1;
    },
    [getFieldConfig],
  );

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const element = pdfRef.current;
      if (!element) {
        showErrorToast("No data found");
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      const pdf = new jsPDF("p", "mm", "a4");

      await pdf.html(element, {
        x: 0,
        y: 0,
        width: 180,
        autoPaging: "text",
        margin: [10, 0, 20, 0],
        pagebreak: { mode: ["css", "legacy", "avoid-all"] },
        html2canvas: {
          scale: 1,
          useCORS: true,
          backgroundColor: "#ffffff",
        },
        callback: function (doc) {
          const totalPages = doc.getNumberOfPages();

          for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);

            doc.setFontSize(8);
            doc.setTextColor(150);

            doc.text(
              `Page ${i} of ${totalPages}`,
              doc.internal.pageSize.getWidth() / 2,
              doc.internal.pageSize.getHeight() - 5,
              { align: "center" }
            );
          }

          doc.save("ServiceDocumentation.pdf");
        },
      });

      showSuccessToast("PDF downloaded successfully!");
    } catch (err) {
      console.error("PDF ERROR:", err);
      showErrorToast("PDF failed");
    }
    setIsDownloading(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const updatedServiceId = params.get("serviceId");

    if (updatedServiceId) {
      setServiceId(updatedServiceId);
      localStorage.setItem("lastServiceId", updatedServiceId);
    } else {
      const savedServiceId = localStorage.getItem("lastServiceId");
      if (savedServiceId) {
        setServiceId(savedServiceId);
      }
    }
  }, [window.location.search]);

  useEffect(() => {
    const getProfile = async () => {
      try {
        const response = await fetchProfile();
        setProfileData(response?.data || "");
      } catch (err) {
        console.error("Profile fetch failed:", err);
      }
    };
    getProfile();
  }, []);

  // ✅ MODIFIED: Check for methods structure
  useEffect(() => {
    const getDocumentation = async () => {
      if (!serviceId) return;
      try {
        const response = await fetchServiceDocumentation(serviceId);
        setDocumentationData(response?.data);

        // ✅ Check if request has methods
        try {
          const parsedRequest = JSON.parse(response?.data?.request || "{}");
          if (
            parsedRequest.methods &&
            typeof parsedRequest.methods === "object"
          ) {
            setHasMethods(true);
            const methodNames = Object.keys(parsedRequest.methods);
            setAvailableMethods(methodNames);
            // Auto-select first method
            if (methodNames.length > 0) {
              setSelectedMethod(methodNames[0]);
            }
          } else {
            setHasMethods(false);
          }
        } catch (e) {
          console.error("Error parsing request:", e);
          setHasMethods(false);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    getDocumentation();
  }, [serviceId]);

  const [failedResponseData, setFailedResponseData] = useState([]);
  const [failedStatusCodes, setFailedStatusCodes] = useState([]);
  const [failedIndex, setFailedIndex] = useState(0);

  useEffect(() => {
    if (documentationData?.failed_response) {
      try {
        const parsed = JSON.parse(`[${documentationData.failed_response}]`);
        setFailedResponseData(parsed);
        setFailedStatusCodes(
          parsed.map((item) => {
            return (
              item?.status?.code ||
              item?.http_response_code ||
              item?.status_code ||
              item?.status
            );
          }),
        );
      } catch (e) {
        console.error("Invalid JSON from backend");
      }
    }


  }, [documentationData]);


  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!documentationData) {
    return <div>No documentation available.</div>;
  }

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showSuccessToast("Copied");
      })
      .catch(() => {
        const tempTextarea = document.createElement("textarea");
        tempTextarea.value = text;
        document.body.appendChild(tempTextarea);
        tempTextarea.select();
        document.execCommand("copy");
        document.body.removeChild(tempTextarea);
        showSuccessToast("Copied");
      });
  };

  const handleEndPointCopy = () => {
    if (inputRef.current) {
      inputRef.current.select();
      navigator.clipboard
        .writeText(inputRef.current.value)
        .then(() => {
          showSuccessToast("Copied to clipboard!");
        })
        .catch((err) => {
          showErrorToast("Failed to copy: ", err);
        });
    }
  };

  const handleCopydata = (ref) => {
    if (ref.current) {
      navigator.clipboard
        .writeText(ref.current.value)
        .then(() => showSuccessToast("Copied to clipboard!"))
        .catch((err) => showErrorToast("Failed to copy: " + err));
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ NEW: Handle method change
  const handleMethodChange = (e) => {
    setSelectedMethod(e.target.value);
    setFormData({}); // Reset form data when method changes
    setResponseData(null);
    setErrorData(null);
  };

  const getBaseUrl = () => {
    const env = documentationData?.environment?.toUpperCase();

    if (env === "PRODUCTION") {
      return "https://javabackend.idspay.in/api/v1/prod";
    }
    if (env === "UAT") {
      return "https://javabackend.idspay.in/api/v1/uat";
    }
    return "";
  };

  // ✅ Validate required fields before sending request
  const handleSendRequest = async () => {
    if (!documentationData?.end_point) {
      return showErrorToast("No endpoint found");
    }

    // ✅ SAFE VALIDATION - No more trim() errors
    const allMissingFields = [];

    // Auth fields
    ["api_id", "api_key", "token_id"].forEach((field) => {
      if (isFieldRequired(field) && !(getAuthFieldValue(field) || "").trim()) {
        allMissingFields.push(field.replace(/_/g, " ").toUpperCase());
      }
    });

    // Dynamic fields
    requestFields.forEach((field) => {
      if (isFieldRequired(field) && !(formData[field] || "").trim()) {
        allMissingFields.push(field.replace(/_/g, " "));
      }
    });

    if (allMissingFields.length > 0) {
      showErrorToast(`Required: ${allMissingFields.join(", ")}`);
      return;
    }

    if (isRequesting) return;
    setIsRequesting(true);

    let payload = {};

    if (hasMethods && selectedMethod) {
      payload = {
        api_id: profileData.user_id || "",
        api_key: profileData.api_key || "",
        token_id: profileData.token_id || "",
        methodName: selectedMethod,
        ...formData,
      };
    } else {
      payload = {
        api_id: profileData.user_id || "",
        api_key: profileData.api_key || "",
        token_id: profileData.token_id || "",
        ...formData,
      };
    }

    try {
      const baseUrl = getBaseUrl();
      const url = `${baseUrl}${documentationData.end_point}`;

      const res = await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });

      let methodResponse = res.data;

      if (hasMethods && selectedMethod && res.data?.methods?.[selectedMethod]) {
        methodResponse = res.data.methods[selectedMethod];
      }

      setResponseData(methodResponse);
      setErrorData(null);

      if (methodResponse?.status?.type === "success") {
        showSuccessToast(
          methodResponse?.status?.message || "API request successful!",
        );
      }
    } catch (err) {
      const errorResponse = err.response?.data;

      let methodError = errorResponse;
      if (
        hasMethods &&
        selectedMethod &&
        errorResponse?.methods?.[selectedMethod]
      ) {
        methodError = errorResponse.methods[selectedMethod];
      }

      if (methodError?.status?.code === 403) {
        showErrorToast(
          methodError?.status?.message || "Unauthorized IP address!",
        );
      } else {
        showErrorToast(
          methodError?.status?.message ||
          methodError?.message ||
          err.message ||
          "API request failed!",
        );
      }

      setResponseData(null);
      setErrorData(methodError || errorResponse || err.message);
    } finally {
      setIsRequesting(false);
    }
  };

  // ✅ Helper for auth field values
  const getAuthFieldValue = (field) => {
    switch (field) {
      case "api_id":
        return profileData?.user_id;
      case "api_key":
        return profileData?.api_key;
      case "token_id":
        return profileData?.token_id;
      default:
        return "";
    }
  };

  // ✅ MODIFIED: Get request fields based on method or direct
  let requestFields = [];

  try {
    const parsedRequest =
      documentationData && documentationData.request
        ? JSON.parse(documentationData.request)
        : {};

    if (
      hasMethods &&
      selectedMethod &&
      parsedRequest.methods &&
      parsedRequest.methods[selectedMethod]
    ) {
      // Get fields for selected method
      const methodFields = parsedRequest.methods[selectedMethod];
      requestFields = Object.keys(methodFields).filter(
        (key) => !["api_id", "api_key", "token_id", "methodName"].includes(key),
      );
    } else if (!hasMethods) {
      // Original logic for non-method APIs
      requestFields = Object.keys(parsedRequest).filter(
        (key) => !["api_id", "api_key", "token_id"].includes(key),
      );
    }
  } catch (error) {
    console.error("Invalid request format:", error);
    requestFields = [];
  }

  const methodColor = {
    GET: "text-green-600",
    POST: "text-orange-500",
    PUT: "text-blue-600",
    PATCH: "text-purple-600",
    DELETE: "text-red-600",
    HEAD: "text-lime-600",
    OPTIONS: "text-pink-600",
  };

  const parsedLiveResponse =
    typeof responseData === "string"
      ? JSON.parse(responseData || "{}")
      : responseData;

  const success =
    parsedLiveResponse?.message === "success" ||
    parsedLiveResponse?.status === "OK" ||
    parsedLiveResponse?.result === "OK" ||
    parsedLiveResponse?.statusCode === 200 ||
    parsedLiveResponse?.code === "00";

  const pdfUrl = parsedLiveResponse?.data?.pdf_url || null;

  const getParsedErrors = () => {
    try {
      let raw = documentationData?.failed_response;

      if (!raw) return [];


      const fixed = `[${raw.replace(/},\s*{/g, "},{")}]`;

      return JSON.parse(fixed);
    } catch (e) {
      console.error("Parsing error:", e);
      return [];
    }
  };


  const parseIntroduction = (htmlString) => {
    if (!htmlString) return { overview: "", benefits: [] };

    const text = htmlString.replace(/<[^>]+>/g, "");

    const parts = text.split("Key Benefits:");

    const overview = parts[0]
      .replace("Overview", "")
      .trim();

    const benefits = parts[1]
      ? parts[1]
        .split("•")
        .map((b) => b.trim())
        .filter((b) => b.length > 0)
      : [];

    return { overview, benefits };
  };

  const { overview, benefits } = parseIntroduction(
    documentationData?.introduction
  );

  return (
    <>
      <Header heading="" value={documentationData?.service_name} />

      <div className="flex items-center justify-between w-full">
        <div className="flex justify-center flex-1">
          <button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className="flex items-center gap-1 bg-blue-600 text-white px-1 py-0 rounded-md shadow hover:bg-blue-700 disabled:opacity-50"
          >
            <IoMdDownload className="text-md" />
            <span>{isDownloading ? "Downloading..." : "API Document"}</span>
          </button>
        </div>

        <Link
          to="/ServicePage"
          className="font-bold text-lg md:text-2xl underline text-blue-700 hover:text-blue-900 transition ml-4"
        >
          ← Back to Service
        </Link>
      </div>

      <div className="bg-gray-100 flex items-start justify-center px-2 md:px-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full max-w-7xl">
          {/* LEFT SIDE */}
          <div className="bg-white p-2 rounded-lg shadow-md overflow-y-auto max-h-[74vh]">
            <div className="border rounded-xl shadow-md border-blue-300 shadow-blue-600 p-1">
              <h3 className="font-semibold text-lg mb-1 flex items-center gap-2 text-gray-800">
                🧪 Test This API
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 border-t pt-1">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Environment
                  </label>
                  <select
                    value={documentationData?.environment}
                    disabled
                    className="border rounded-md p-2 text-sm w-full bg-gray-200"
                  >
                    <option value="PRODUCTION">PRODUCTION</option>
                    <option value="UAT">UAT</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Method
                  </label>
                  <select
                    value={documentationData?.method}
                    disabled
                    className="border rounded-md p-2 text-sm w-full bg-gray-200"
                  >
                    <option value="POST">POST</option>
                    <option value="GET">GET</option>
                    <option value="PATCH">PATCH</option>
                    <option value="HEAD">HEAD</option>
                    <option value="OPTIONS">OPTIONS</option>
                    <option value="DELETE">DELETE</option>
                    <option value="PUT">PUT</option>
                  </select>
                </div>

                {/*  NEW: Method Selection Dropdown (Step 1) */}
                {hasMethods && availableMethods.length > 0 && (
                  <div className="">
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      🔧 Select Method
                    </label>
                    <select
                      value={selectedMethod}
                      onChange={handleMethodChange}
                      className="border rounded-md p-2 text-sm w-full bg-white focus:ring-2 focus:ring-blue-400"
                    >
                      {availableMethods.map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/*  UPDATED: Static auth fields with required indicators */}
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    API ID
                    {/* {isFieldRequired('api_id') && (
            <span className="text-red-500 ml-1" aria-label="required">*</span>
        )} */}
                  </label>
                  <div className="relative">
                    <input
                      ref={apiIdRef}
                      readOnly
                      value={profileData?.user_id || ""}
                      className={`border rounded-md p-2 pr-8 text-gray-700 w-full transition-all
                ${isFieldReadOnly("api_id")
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : isFieldRequired("api_id")
                            ? "focus:ring-red-200 bg-gray-50"
                            : "focus:ring-blue-400 focus:border-blue-500 border-gray-300 bg-gray-50"
                        }`}
                    />
                    <FaCopy
                      className="absolute right-2 top-2.5 text-gray-500 hover:text-blue-600 cursor-pointer"
                      onClick={() => handleCopydata(apiIdRef)}
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    API Key
                    {/* {isFieldRequired('api_key') && (
            <span className="text-red-500 ml-1" aria-label="required">*</span>
        )} */}
                  </label>
                  <div className="relative">
                    <input
                      ref={apiKeyRef}
                      readOnly
                      value={profileData?.api_key || ""}
                      className={`border rounded-md p-2 pr-8 text-gray-700 w-full transition-all
                ${isFieldReadOnly("api_key")
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : isFieldRequired("api_key")
                            ? "focus:ring-red-200 bg-gray-50"
                            : "focus:ring-blue-400 focus:border-blue-500 border-gray-300 bg-gray-50"
                        }`}
                    />
                    <FaCopy
                      className="absolute right-2 top-2.5 text-gray-500 hover:text-blue-600 cursor-pointer"
                      onClick={() => handleCopydata(apiKeyRef)}
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Token ID
                    {/* {isFieldRequired('token_id') && (
            <span className="text-red-500 ml-1" aria-label="required">*</span>
        )} */}
                  </label>
                  <div className="relative">
                    <input
                      ref={tokenIdRef}
                      readOnly
                      value={profileData?.token_id || ""}
                      className={`border rounded-md p-2 pr-8 text-gray-700 w-full transition-all
                ${isFieldReadOnly("token_id")
                          ? "bg-gray-100 cursor-not-allowed border-gray-300"
                          : isFieldRequired("token_id")
                            ? "focus:ring-red-200 bg-gray-50"
                            : "focus:ring-blue-400 focus:border-blue-500 border-gray-300 bg-gray-50"
                        }`}
                    />
                    <FaCopy
                      className="absolute right-2 top-2.5 text-gray-500 hover:text-blue-600 cursor-pointer"
                      onClick={() => handleCopydata(tokenIdRef)}
                    />
                  </div>
                </div>
              </div>

              {requestFields?.length > 0 && (
                <div className="mt-2 border-t pt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {hasMethods && (
                    <div className="sm:col-span-2 mb-1">
                      <p className="text-sm font-semibold text-blue-700">
                        📋 Fields for method:{" "}
                        <span className="text-gray-800">{selectedMethod}</span>
                      </p>
                      <div className="flex gap-4 text-xs mt-1">
                        {/* <span className="text-red-500 flex items-center gap-1">
                        <span>*</span> Required
                    </span> */}
                        {/* <span className="text-blue-500 flex items-center gap-1">
                         Readonly
                    </span> */}
                      </div>
                    </div>
                  )}
                  {requestFields.map((field, idx) => {
                    const required = isFieldRequired(field);
                    const readonly = isFieldReadOnly(field);

                    return (
                      <div key={idx}>
                        <label
                          htmlFor={field}
                          className="block text-sm font-medium text-gray-700 mb-1 capitalize items-center gap-1"
                        >
                          <span>{field.replace(/_/g, " ")}</span>
                          {required && (
                            <span
                              className="text-red-500 text-md font-semibold"
                              aria-label="required"
                            >
                              *
                            </span>
                          )}
                          {/* {readonly && (
                            <span className="text-blue-500 text-xs font-medium" aria-label="readonly">🔒</span>
                        )} */}
                          {!required && !readonly && (
                            <span className="text-gray-400 text-xs ml-1 font-normal">
                              (optional)
                            </span>
                          )}
                        </label>
                        <input
                          id={field}
                          name={field}
                          placeholder={`Enter ${field.replace(/_/g, " ")}`}
                          value={formData[field] || ""}
                          onChange={handleInputChange}
                          readOnly={readonly}
                          required={required}
                          className={`border rounded-md p-2 text-sm w-full focus:ring-2 bg-gray-50 transition-all
                            ${readonly
                              ? "bg-gray-100 cursor-not-allowed border-gray-300 text-gray-500"
                              : required
                                ? "bg-white ring-1 border"
                                : "focus:ring-blue-400 focus:border-blue-500 border-gray-300"
                            }`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              <span className="mr-4 text-sm text-gray-600 text-center font-semibold mt-2 block">
                👉 Response will reflect in the response section
              </span>

              <div className="flex justify-center mt-5 ">
                <button
                  onClick={handleSendRequest}
                  disabled={isRequesting}
                  className={`px-2 py-0 rounded-md text-white font-semibold transition-all ${isRequesting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 shadow-md"
                    }`}
                >
                  {isRequesting ? "Sending..." : "Send Request"}
                </button>
                <button
                  disabled={!success || !pdfUrl}
                  onClick={() => window.open(pdfUrl, "_blank")}
                  className={`flex items-center gap-1 px-1 py-0 rounded-md ms-4 shadow
        ${success && pdfUrl
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed text-white"
                    }`}
                >
                  <IoMdDownload className="text-md" />
                  <span>PDF Report</span>
                </button>
              </div>
            </div>

            <div className="mb-1 mt-4">
              <p>
                <strong>Service Name:</strong>{" "}
                {documentationData?.service_name || ""}
              </p>
            </div>

            <h3 className="text-start underline font-semibold text-black">
              Environment
            </h3>
            <div>
              <h5>
                {" "}
                Credentials will be same for production and UAT Environment.
                Need to change only base URL as per the Environment with
                Endpoint.
              </h5>
            </div>

            <p className="mb-1">
              <strong>Base Url for Production :</strong>
              <span className="text-blue-600 break-words ml-1">
                https://javabackend.idspay.in/api/v1/prod
              </span>
            </p>

            <p className="mb-1">
              <strong>Base Url for UAT :</strong>
              <span className="text-blue-600 break-words ml-1">
                https://javabackend.idspay.in/api/v1/uat
              </span>
            </p>

            <div className="mb-1 mt-2">
              <p>
                <strong>Current Environment Status:</strong>{" "}
                <span className="text-blue-600 break-words">
                  {" "}
                  {documentationData?.environment || ""}
                </span>
              </p>
            </div>

            <div className="mb-1 mt-2">
              <p>
                <strong>Endpoint:</strong>{" "}
                <a
                  href={documentationData?.end_point}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline"
                >
                  {documentationData?.end_point || ""}
                </a>
              </p>
            </div>

            <div className="prose prose-sm max-w-none border rounded-xl shadow-md border-blue-300 shadow-blue-600 px-3 py-2">
              <ul className="list-disc pl-4 space-y-2">
                <li>
                  <strong>API ID :</strong> Login ID will be the API ID
                  <span className="italic">(Example: APID03XXXX)</span>
                </li>
                <li>
                  <strong>API Key :</strong> You can find the API Key from the
                  IP Whitelist Menu
                  <span className="italic">
                    (Example: 7&XXXX-XXXXX-XXXXXX-XXXX)
                  </span>
                </li>
                <li>
                  <strong>Token ID :</strong> You can get the Token ID once the
                  IP has been whitelisted from the IP Whitelist Menu. Token ID
                  will be different for each IP Address
                  <span className="italic">(Example: 7&XXXXXXXXXXX)</span>
                </li>
                <li>
                  <strong>Account Status :</strong> Make sure the API account is
                  activated before service utilization.
                </li>
              </ul>
            </div>

            <div className="mb-1 mt-2">
              <p>
                <strong>Introduction:</strong>
              </p>
              <div
                className="prose prose-sm max-w-none border rounded-xl shadow-md border-blue-300 shadow-blue-600 px-2"
                dangerouslySetInnerHTML={{
                  __html: documentationData?.introduction,
                }}
              />
            </div>

            <div className="prose prose-sm max-w-none border rounded-xl shadow-md border-blue-300 shadow-blue-600 px-3 py-2">
              <p className="text-gray-700">
                <strong>Description :</strong>{" "}
                {documentationData?.service_value || ""}
              </p>
            </div>

            <div className="mt-6 hidden">
              <div
                ref={pdfRef}
                style={{
                  position: "static",
                  top: 0,
                  left: 0,
                  width: "210px",
                  background: "#ffffff",
                  color: "#000000",
                  padding: "0px",
                  transformOrigin: "top left",
                  fontSize: "5px",
                  margin: "0px",
                  justifyContent: "space-between",

                }}
              >
                <div>
                  <div
                    style={{
                      background: "#2563EB",
                      text: "white",
                      paddingLeft: "8px",
                      padding: "0px 8px 8px 8px",
                    }}
                  >
                    <p
                      style={{
                        color: "white",
                        fontSize: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      IDSPay API Technical Documentation
                    </p>
                    <p
                      style={{
                        color: "#e1e6f0",
                        fontSize: "4px",
                        paddingTop: "2px",
                      }}
                    >
                      Enterprise API Platform for Verification, Onboarding &
                      Automation
                    </p>
                    <p
                      style={{
                        color: "#e1e6f0",
                        fontSize: "3px",
                        paddingTop: "2px",
                      }}
                    >
                      Version 1.0 | Updated{" "}
                      {documentationData?.updated_at
                        ? new Date(
                          documentationData.updated_at,
                        ).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                        : ""}
                    </p>
                  </div>

                  <div>
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "5px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 2px 8px",
                      }}
                    >

                      <div className="text-[5px] font-bold text-gray-900 underline">
                        {" "}
                        Service Name:- {documentationData?.service_name || " "} Technical Document
                      </div>
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "5px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 2px 8px",
                      }}
                    >
                      Company Introduction
                    </div>
                    <p className="text-[4px] pl-2 pr-2 pb-1 text-[#374151]">
                      IDSPay provides enterprise-grade APIs enabling
                      organizations to automate identity verification, digital
                      onboarding, financial workflows and data intelligence
                      services. Our platform supports fintech companies, banks,
                      NBFCs, startups and enterprises seeking scalable API
                      integrations.
                    </p>
                  </div>


                  <div
                    style={{ padding: "8px" }}
                    className="m-2 border bg-[#EFF6FF] rounded-[3px]"
                  >
                    <div style={{ pageBreakInside: "avoid" }}>
                      <div
                        style={{
                          fontSize: "5px",
                          fontWeight: "bold",
                          color: "#1D4ED8",
                        }}
                      >
                        Server Introduction:
                      </div>
                      <div className="text-gray-900 font-bold mt-4">Overview</div>

                      {/* OVERVIEW */}
                      <div style={{ fontSize: "4px", marginTop: "4px" }}>

                        <p style={{ marginTop: "2px", lineHeight: "1.6" }}>
                          {overview}
                        </p>
                      </div>
                      <div className="text-gray-900 font-bold mt-4">Key Benefits:</div>

                      {/* KEY BENEFITS */}
                      {benefits.length > 0 && (
                        <div style={{ fontSize: "4px", marginTop: "6px" }}>


                          <div style={{ marginTop: "3px" }}>
                            {benefits.map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  gap: "3px",
                                  marginBottom: "2px",
                                  lineHeight: "1.5",
                                }}
                              >
                                <span>•</span>
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>


                  <div
                    className="mt-4"
                    style={{ pageBreakBefore: "auto" }}
                  ></div>

                  <div
                    style={{ padding: "8px 8px 8px 8px" }}
                    className="m-2 shadow-xs border-[0.1px] bg-[#EFF6FF] rounded-[3px] border-[#cacdd3]"
                  >
                    <div
                      style={{
                        fontSize: "5px",
                        fontWeight: "bold",
                        color: "#1D4ED8",
                      }}
                    >
                      Description:
                    </div>

                    <div className=" text-[4px] text-[#374151]">
                      {" "}
                      {documentationData?.service_value || " "}
                    </div>


                  </div>

                  <div className="mt-2" style={{ pageBreakBefore: "always" }}>
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "5px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 8px 8px",
                      }}
                    >
                      API Document Summary
                    </div>

                    <div className="grid grid-cols-3 gap-1 pl-2 pr-2">
                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3]"
                      >
                        <div className="text-[3px] text-[#6B7280]">
                          API Name
                        </div>
                        <div className="font-semibold text-[4px] text-[#111827]">
                          {" "}
                          {documentationData?.service_name || " "}
                        </div>
                      </div>

                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3]"
                      >
                        <div className="text-[3px] text-[#6B7280]">
                          Category
                        </div>
                        <div className="font-semibold text-[4px] text-[#111827]">
                          {" "}
                          {documentationData?.service_category_name || " "}
                        </div>
                      </div>

                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3]"
                      >
                        <div className="text-[3px] text-[#6B7280]">Version</div>
                        <div className="font-semibold text-[4px] text-[#111827]">
                          {" "}
                          v1.0
                        </div>
                      </div>

                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3]"
                      >
                        <div className="text-[3px] text-[#6B7280]">
                          HTTP Method
                        </div>
                        <div className="font-semibold text-[4px] text-[#4da158]">
                          {" "}
                          {documentationData?.method || " "}
                        </div>
                      </div>

                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3]"
                      >
                        <div className="text-[3px] text-[#6B7280]">
                          Endpoint
                        </div>
                        <div className=" text-[3px] text-[#2655bb]">
                          {" "}
                          {documentationData?.end_point || " "}
                        </div>
                      </div>
                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3]"
                      >
                        <div className="text-[3px] text-[#6B7280]">
                          Last Updated
                        </div>
                        <div className="font-semibold text-[4px] text-[#111827]">
                          {documentationData?.updated_at
                            ? new Date(
                              documentationData.updated_at,
                            ).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            : ""}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "5px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 8px 8px",
                      }}
                    >
                      Base URL
                    </div>

                    <div className="grid grid-cols-2 gap-1 pl-2 pr-2">
                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3] bg-[#edeff3]"
                      >
                        <div className="text-[3px] text-[#6B7280]">
                          UAT Environment
                        </div>
                        <div className="font-semibold text-[4px] text-[#2655bb]">
                          {" "}
                          https://javabackend.idspay.in/api/v1/prod
                        </div>
                      </div>

                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3] bg-[#edeff3]"
                      >
                        <div className="text-[3px] text-[#6B7280]">
                          Production Environment
                        </div>
                        <div className="font-semibold text-[4px] text-[#2655bb]">
                          {" "}
                          https://javabackend.idspay.in/api/v1/uat
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "5px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 8px 8px",
                      }}
                    >
                      API Endpoint
                    </div>

                    <div className="pl-2 pr-2">
                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="flex flex-row gap-2 rounded-[3px] items-center bg-[#EFF6FF]"
                      >
                        {/* <div className="text-[4px] text-[#4ADE80]">
                          {documentationData?.method || " "}
                        </div> */}
                        <div className="font-semibold text-[4px] text-gray-900">
                          {documentationData?.end_point || " "}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "4px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 8px 8px",
                      }}
                    >
                      Request Sample
                    </div>

                    <div className="pl-2 pr-2">
                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="flex flex-row gap-2 rounded-[3px] items-center bg-[#EFF6FF]"
                      >
                        <pre
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: "4px",
                            lineHeight: "8px",
                          }}
                          className=" rounded-md text-sm text-gray-900"
                        >
                          {(() => {
                            try {
                              const parsedResponse = JSON.parse(
                                documentationData?.request || "{}",
                              );
                              const liveReq = {
                                api_id: profileData?.user_id || "",
                                api_key: profileData?.api_key || "",
                                token_id: profileData?.token_id || "",
                                ...(hasMethods && {
                                  methodName: selectedMethod,
                                }),
                                ...formData,
                              };
                              return JSON.stringify(parsedResponse, null, 2);
                            } catch {
                              return (
                                documentationData?.request ||
                                "No request provided"
                              );
                            }
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "4px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 8px 8px",
                      }}
                    >
                      Response Sample
                    </div>

                    <div className="pl-2 pr-2">
                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="flex flex-row gap-2 rounded-[3px] items-center bg-[#EFF6FF]"
                      >
                        <pre
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: "4px",
                            lineHeight: "8px",
                          }}
                          className="rounded-md text-sm text-gray-900"
                        >
                          {(() => {
                            if (responseData) {
                              return JSON.stringify(responseData, null, 1);
                            }

                            try {
                              const parsedResponse = JSON.parse(
                                documentationData?.response || "{}",
                              );
                              if (
                                hasMethods &&
                                selectedMethod &&
                                parsedResponse.methods?.[selectedMethod]
                              ) {
                                return JSON.stringify(
                                  parsedResponse.methods[selectedMethod],
                                  null,
                                  1,
                                );
                              }
                              return (
                                documentationData?.response ||
                                "No response provided"
                              );
                            } catch {
                              return (
                                documentationData?.response ||
                                "No response provided"
                              );
                            }
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "4px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 8px 8px",
                      }}
                    >
                      Error Response Sample
                    </div>

                    <div className="pl-2 pr-2 flex flex-col gap-2">
                      {getParsedErrors().length > 0 ? (
                        getParsedErrors().map((err, index) => {
                          const code =
                            err?.status?.code || err?.http_response_code;

                          return (
                            <div
                              key={index}
                              className="bg-[#EFF6FF] text-[#d43131] rounded-[3px] p-2"
                            >
                              {/* Title */}
                              <div
                                style={{ fontSize: "4px", fontWeight: "bold" }}
                              >
                                Error Code: {code || "N/A"}
                              </div>

                              {/* Message */}
                              <div
                                style={{ fontSize: "4px", marginBottom: "4px" }}
                              >
                                {err?.status?.message ||
                                  err?.message ||
                                  err?.error}
                              </div>

                              {/* JSON */}
                              <pre
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  fontSize: "4px",
                                }}
                              >
                                {JSON.stringify(err, null, 2)}
                              </pre>
                            </div>
                          );
                        })
                      ) : (
                        <div>No failed response provided</div>
                      )}
                    </div>
                  </div>

                  <div className="">
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "5px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 8px 8px",
                      }}
                    >
                      Possible Error Codes
                    </div>

                    <div className="px-2 ">
                      <table className="w-full border-[0.1px] border-[#cacdd3] table-fixed">
                        <thead className="bg-[#F3F4F6] ">
                          <tr>
                            <th className=" px-1 py-1 text-left text-[4px] w-[25%]">
                              Error Code
                            </th>
                            <th className=" px-1 py-1 text-left text-[4px] w-[20%]">
                              HTTP Status
                            </th>
                            <th className=" px-1 py-1 text-left text-[4px] w-[55%]">
                              Description
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {getParsedErrors().map((err, index) => {
                            const httpCode =
                              err?.status?.code ||
                              err?.http_response_code ||
                              "N/A";

                            const errorCode =
                              err?.data?.code ||
                              err?.result_code ||
                              "UNKNOWN_ERROR";

                            const description =
                              err?.status?.message ||
                              err?.message ||
                              err?.error ||
                              "No description";

                            return (
                              <tr key={index}>
                                <td className="px-1 py-1 text-[4px] break-words">
                                  {errorCode}
                                </td>
                                <td className="px-1 py-1 text-[4px] break-words">
                                  {httpCode}
                                </td>
                                <td className="px-1 py-1 text-[4px] break-words">
                                  {description}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>



                  <div>
                    <div
                      style={{
                        color: "#111827",
                        fontSize: "5px",
                        fontWeight: "medium",
                        paddingLeft: "8px",
                        padding: "4px 8px 8px 8px",
                      }}
                    >
                      Basic Integration Information
                    </div>

                    <div className="pl-2 pr-2">
                      <div
                        style={{ padding: "0px 4px 6px 4px" }}
                        className="shadow-xs border-[0.1px]  rounded-[3px] items-center border-[#cacdd3] bg-[#EFF6FF]"
                      >
                        <div className="text-[4px]">
                          API access requires a valid API key issued by IDSPay.
                        </div>
                        <div className=" text-[4px]">
                          All requests must be sent via HTTPS.
                        </div>
                        <div className="text-[4px]">
                          Content-Type must be application/json.
                        </div>
                        <div className=" text-[4px]">
                          Use the UAT environment for testing before production.
                        </div>
                        <div className=" text-[4px]">
                          Integration support is available from the IDSPay
                          technical team.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className=" mt-2 items-center text-center grid grid-cols-3"
                  style={{
                    background: "#F9FAFB",
                    paddingLeft: "8px",
                    padding: "4px 8px 8px 8px",
                  }}
                >
                  <div>
                    <div style={{ padding: "4px 8px" }}>
                      <p
                        style={{
                          color: "#111827",
                          fontSize: "4px",
                          fontWeight: "bold",
                          marginBottom: "2px",
                        }}
                      >
                        Visit Our Website
                      </p>

                      <a
                        href="https://www.idspay.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#2563EB",
                          fontSize: "3px",
                        
                          wordBreak: "break-all",
                        }}
                      >
                        www.idspay.in
                      </a>
                    </div>
                  </div>
                  <div>

                    <p
                      style={{
                        color: "#111827",
                        fontSize: "4px",
                        fontWeight: "bold",
                      }}
                    >
                      IDSPay Enterprise API Platform
                    </p>

                    <p
                      style={{
                        color: "#1F2937",
                        fontSize: "4px",
                        paddingTop: "2px",
                      }}
                    >
                      www.idspay.in | support@idspay.in
                    </p>
                    <p
                      style={{
                        color: "#111827",
                        fontSize: "3px",
                        paddingTop: "2px",
                      }}
                    >
                      © 2026 IDSPay. All Rights Reserved.
                    </p>

                  </div>
                  <div>

                    <div style={{ padding: "4px 8px" }}>
                      <p
                        style={{
                          color: "#111827",
                          fontSize: "4px",
                          fontWeight: "bold",
                          marginBottom: "2px",
                        }}
                      >
                        Create Your API Account
                      </p>

                      <a
                        href="https://www.idspay.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#2563EB",
                          fontSize: "3px",
                      
                          wordBreak: "break-all",
                        }}
                      >
                        www.apiuser.idspay.in/SignUpForm
                      </a>
                    </div>

                  </div>


                </div>

                <div
                  className=" mt-2 items-center text-center"
                  style={{
                    paddingLeft: "8px",
                    padding: "4px 8px 8px 8px",
                  }}
                >
                  <p
                    style={{
                      color: "#111827",
                      fontSize: "4px",
                      fontWeight: "bold",
                    }}
                  >
                    -- End Of The Document --
                  </p>

                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <aside className="bg-black text-white p-2 rounded-lg font-mono overflow-y-auto max-h-[74vh]">
            <div className="flex space-x-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>

            <label className="font-bold block mb-2">Endpoint</label>
            <div className="flex items-center mb-2 gap-2">
              <div className="flex flex-1 items-center border rounded-md overflow-hidden">
                {/* Method */}
                <span
                  className={`px-3 py-2 font-bold text-sm whitespace-nowrap ${methodColor[documentationData?.method] || "text-gray-700"}`}
                >
                  {documentationData?.method}
                </span>
                {/* Endpoint */}
                <input
                  ref={inputRef}
                  type="text"
                  value={documentationData?.end_point || ""}
                  className="flex-1 p-2 text-black outline-none"
                  readOnly
                />
              </div>

              {/* Copy Button */}
              <button
                onClick={handleEndPointCopy}
                className="bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-white hover:text-black transition"
              >
                Copy
              </button>
            </div>

            <label className="font-bold block mb-2">Content Type</label>
            <select className="w-full md:w-1/2 rounded-md p-2 mb-4 text-black">
              <option value="application/json">application/json</option>
            </select>

            {/* Request */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <strong>Request:</strong>
                <button
                  onClick={() =>
                    handleCopy(
                      documentationData?.request || "No request provided",
                    )
                  }
                  className=" text-white p-2 rounded-md hover:bg-white hover:text-black transition"
                >
                  <CopyIcon />
                </button>
              </div>
              <pre className="bg-gray-800 text-green-400 p-2 rounded-md whitespace-pre-wrap">
                {documentationData?.request || "No request provided"}
              </pre>
            </div>

            {/* Response */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <strong>Response:</strong>
                <button
                  onClick={() => {
                    const displayResponse =
                      responseData ||
                      documentationData?.response ||
                      "No response provided";
                    const responseText =
                      typeof displayResponse === "string"
                        ? displayResponse
                        : JSON.stringify(displayResponse, null, 2);
                    handleCopy(responseText);
                  }}
                  className=" text-white p-2 rounded-md hover:bg-white hover:text-black transition"
                >
                  <CopyIcon />
                </button>
              </div>
              <pre className="bg-gray-800 text-green-400 p-2 rounded-md whitespace-pre-wrap">
                {(() => {
                  if (responseData) {
                    // ✅ Format response with proper structure
                    return JSON.stringify(responseData, null, 2);
                  }

                  // Show documentation response
                  try {
                    const parsedResponse = JSON.parse(
                      documentationData?.response || "{}",
                    );

                    // If methods exist, show selected method's response
                    if (
                      hasMethods &&
                      selectedMethod &&
                      parsedResponse.methods?.[selectedMethod]
                    ) {
                      return JSON.stringify(
                        parsedResponse.methods[selectedMethod],
                        null,
                        2,
                      );
                    }

                    return (
                      documentationData?.response || "No response provided"
                    );
                  } catch {
                    return (
                      documentationData?.response || "No response provided"
                    );
                  }
                })()}
              </pre>
            </div>

            {/* Failed Response */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-2 pt-2">
                  <strong>Failed Responses:</strong>
                  {failedStatusCodes && (
                    <div className="flex items-center gap-2 ">
                      {failedStatusCodes?.map((code, i) => (
                        <button
                          onClick={() => setFailedIndex(i)}
                          className={` px-2 py-1 rounded-md  ${failedIndex === i ? "text-red-200 border-red-200 bg-red-800" : "text-gray-200 border-gray-200 bg-gray-800"}`}
                          key={i}
                        >
                          {code}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    const displayError =
                      errorData ||
                      documentationData?.failed_response ||
                      "No failed response provided";
                    const errorText =
                      typeof displayError === "string"
                        ? displayError
                        : JSON.stringify(displayError, null, 2);
                    handleCopy(errorText);
                  }}
                  className=" text-white p-2 rounded-md hover:bg-white hover:text-black transition"
                >
                  <CopyIcon />
                </button>
              </div>
              <pre className="bg-gray-800 p-2 rounded-md whitespace-pre-wrap text-red-600">
                {(() => {
                  if (errorData) {
                    return JSON.stringify(errorData, null, 2);
                  }

                  // Show documentation failed response
                  try {
                    const parsedFailedResponse = JSON.parse(
                      documentationData?.failed_response || "{}",
                    );

                    // If methods exist, show selected method's failed response
                    if (
                      hasMethods &&
                      selectedMethod &&
                      failedResponseData.methods?.[selectedMethod]
                    ) {
                      return JSON.stringify(
                        parsedFailedResponse.methods[selectedMethod],
                        null,
                        2,
                      );
                    }

                    return (
                      JSON.stringify(
                        failedResponseData[failedIndex],
                        null,
                        2,
                      ) || "No failed response provided"
                    );
                  } catch {
                    // return (
                    //   documentationData?.failed_response ||
                    //   "No failed response provided"
                    // );
                    return (
                      JSON.stringify(
                        failedResponseData[failedIndex],
                        null,
                        2,
                      ) || "No failed response provided"
                    );
                  }
                })()}
              </pre>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default ApiLink;
